<!-- TextForm.vue -->
<script setup>
import { useRouter } from "vue-router";
import { ref, reactive, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import debounce from "lodash.debounce";
import { toTitleCase } from "@/utils/stringUtils";
// eslint-disable-next-line no-unused-vars
import { formatDateTime, formatDateTimeForDisplay } from "@/utils/dateUtils";

// Stores
import { useTextStore } from "@/stores/useTextStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useAuthorStore } from "@/stores/useAuthorStore";
import { useCategoryStore } from "@/stores/useCategoryStore";
import { useTopicStore } from "@/stores/useTopicStore";
import { useUserStore } from "@/stores/useUserStore";
// ── NOVO: guard de permissão por vínculo Membro-Titular ───────────────────────
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";

// Componentes modais
import AddTextForm from "./AddTextForm.vue";
import EditTextForm from "./EditTextForm.vue";
import DeleteTextForm from "./DeleteTextForm.vue";
import BatchDeleteTextForm from "./BatchDeleteTextForm.vue";
import TextCardModal from "./TextCardModal.vue";
import TextHelpModal from "./TextHelpModal.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";

const showHelpModal = ref(false);

// Componentes de paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

const router = useRouter();

// Stores
const textStore = useTextStore();
const activityStore = useActivityStore();
const authorStore = useAuthorStore();
const categoryStore = useCategoryStore();
const userStore = useUserStore();
const topicStore = useTopicStore();
// ── NOVO ──────────────────────────────────────────────────────────────────────
const managedAccountStore = useManagedAccountStore();

// ── NOVO: permissões efetivas do vínculo (null = sem restrição) ───────────────
const memberTextPermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.texts ?? null
);

const memberCanViewTexts = computed(
  () => memberTextPermissions.value === null || memberTextPermissions.value.view
);
// Criar/editar/excluir combinam o grid do vínculo com a opção "somente conteúdo
// do titular" (que bloqueia toda escrita de texto pelo membro).
const {
  canCreateText: memberCanCreateText,
  canEditText: memberCanEditText,
  canDeleteText: memberCanDeleteText,
} = useMemberRestrictions();


// ── NOVO: guard de atividades — portão para o botão "Atividades" na linha ─────
const memberActivityPermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.activities ?? null
);
const memberCanViewActivities = computed(
  () =>
    memberActivityPermissions.value === null ||
    memberActivityPermissions.value.view
);

// 🔹 Campos padrão da busca
const DEFAULT_TEXT_SEARCH_FIELDS = [
  "title",
  "subtitle",
  "author",
  "category",
  "topics",
  "creator",
  "source",
];

function resetTextSearchState() {
  textStore.searchTerm = "";
  textStore.textSearchFields = [...DEFAULT_TEXT_SEARCH_FIELDS];
  textStore.currentPage = 1;
  textStore.shouldForcePageOne = false;
  textStore.setMyTextsOnly(false);
  textStore.clearFilters();
}

// --- LÓGICA DO TOAST ---
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 6000);
}

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const showTextCardModal = ref(false);
const selectedTextCardId = ref(null);

const showPermissionModal = ref(false);
const selectedPermissionTextId = ref(null);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);
const isLoading = ref(false);

// Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIdsOnPage = textStore.texts
      .filter((t) => canEdit(t))
      .map((t) => t.textId);
    if (editableIdsOnPage.length === 0) return false;
    return editableIdsOnPage.every((id) =>
      textStore.selectedTextIds.includes(id)
    );
  },
  set: (value) => {
    const editableIdsOnPage = textStore.texts
      .filter((t) => canEdit(t))
      .map((t) => t.textId);
    if (value) {
      editableIdsOnPage.forEach((id) => {
        if (!textStore.selectedTextIds.includes(id))
          textStore.selectedTextIds.push(id);
      });
    } else {
      textStore.selectedTextIds = textStore.selectedTextIds.filter(
        (id) => !editableIdsOnPage.includes(id)
      );
    }
  },
});

const anyEditableOnPage = computed(() =>
  textStore.texts.some((t) => canEdit(t))
);

const canBatchActions = computed(
  () =>
    Array.isArray(textStore.selectedTextIds) &&
    textStore.selectedTextIds.length > 0
);

// ── Posicionamento do menu de ações da linha ────────────────────────────────
//
// O menu é `position: absolute` ancorado na célula, e não teleportado: assim ele
// acompanha a rolagem e nunca se descola da linha. O que faltava era escolher o
// LADO. Aberto sempre para baixo, o menu das últimas linhas passa da borda do
// `.table-wrapper` — que recorta, porque `overflow-x: auto` faz o eixo Y computar
// `auto` — e o fim dele fica cortado.
//
// Mesma solução já em uso no ActivityForm; as classes `opens-up`/`opens-left`
// espelham a ancoragem.
const MENU_EDGE_MARGIN = 8;
const MENU_MIN_HEIGHT = 120;
const ROW_MENU_OFFSET = 42; // igual ao left: 42px de .row-dropdown-menu

const rowMenuPlacement = ref({
  up: false,
  left: false,
  maxHeight: null,
  ready: false,
});

// Quem recorta é o `.table-wrapper`. A janela entra na conta para o caso de o
// wrapper se estender além dela.
function getClipBounds(menuEl) {
  const wrapper = menuEl.closest(".table-wrapper");
  const r = wrapper?.getBoundingClientRect();
  return {
    top: Math.max(r?.top ?? 0, 0) + MENU_EDGE_MARGIN,
    bottom:
      Math.min(r?.bottom ?? window.innerHeight, window.innerHeight) -
      MENU_EDGE_MARGIN,
    left: Math.max(r?.left ?? 0, 0) + MENU_EDGE_MARGIN,
    right:
      Math.min(r?.right ?? window.innerWidth, window.innerWidth) -
      MENU_EDGE_MARGIN,
  };
}

// A altura real só existe depois de o `v-if` renderizar o menu, daí o nextTick.
// Até a medição ele fica com `visibility: hidden`, para não dar um salto visível
// quando a conta decidir abrir para o outro lado.
async function placeRowMenu() {
  await nextTick();
  const menuEl = document.querySelector(".row-dropdown-menu");
  const cell = menuEl?.closest(".action-cell-container");
  if (!menuEl || !cell) return;

  const clip = getClipBounds(menuEl);
  const anchor = cell.getBoundingClientRect();
  const h = menuEl.offsetHeight;
  const w = menuEl.offsetWidth;

  // Vertical: alinhado pelo topo da célula; sem espaço, alinha pelo rodapé.
  const roomBelow = clip.bottom - anchor.top;
  const roomAbove = anchor.bottom - clip.top;
  const up = h > roomBelow && roomAbove > roomBelow;
  const room = up ? roomAbove : roomBelow;

  // Horizontal: à direita da célula; sem espaço, espelha para a esquerda.
  const fitsRight = anchor.left + ROW_MENU_OFFSET + w <= clip.right;
  const fitsLeft = anchor.right - ROW_MENU_OFFSET - w >= clip.left;

  rowMenuPlacement.value = {
    up,
    left: !fitsRight && fitsLeft,
    // Não cabe em nenhum dos lados: limita e rola por dentro do menu.
    maxHeight: h > room ? Math.max(room, MENU_MIN_HEIGHT) : null,
    ready: true,
  };
}

function toggleRowDropdown(textId) {
  const willOpen = openDropdownId.value !== textId;
  openDropdownId.value = willOpen ? textId : null;
  if (!willOpen) return;

  rowMenuPlacement.value = {
    up: false,
    left: false,
    maxHeight: null,
    ready: false,
  };
  placeRowMenu();
}

function handleEdit(index) {
  indexSelector(index);
  // Edição comum: o foco é o de sempre (título), nunca o campo herdado de um
  // item em aberto clicado antes.
  editFocusField.value = null;
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  indexSelector(index);
  selectedTextCardId.value = textStore.idText;
  showTextCardModal.value = true;
  openDropdownId.value = null;
}

function handleViewPermissions(index) {
  indexSelector(index);
  selectedPermissionTextId.value = textStore.idText;
  showPermissionModal.value = true;
  openDropdownId.value = null;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function handleNotify(payload) {
  const type = payload?.type;
  const message = payload?.message || "";
  if (type === "success") showSuccessToast(message);
  else showErrorToast(message);
}

async function handleCardTextUpdated() {
  await handleTextsChanged({
    goToFirstPage: true,
    closeAdd: false,
    closeEdit: false,
    closeDelete: false,
    closeBatchDelete: false,
    clearSelection: true,
  });
}

function toggleSelection(id) {
  textStore.toggleSelection(id);
}

function tryOpenBatchDeleteModal() {
  if (!textStore.selectedTextIds || textStore.selectedTextIds.length === 0) {
    showErrorToast("Nenhum texto selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }
  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// Abrir um texto leva direto à tela de anotação: a série ("atividade") é
// resolvida por trás (última usada ou a padrão "Anotações gerais"). A tela de
// gestão de séries continua acessível pelo seletor no cabeçalho da anotação.
async function goToActivities(textId) {
  try {
    await activityStore.openTextForAnnotation(textId);

    router.push({ name: "RightArea" });
  } catch (error) {
    const status = error?.response?.status;
    if (status === 403) {
      showErrorToast(
        error.response?.data?.message ||
          "Seu vínculo não permite anotar neste texto."
      );
    } else {
      showErrorToast(
        error.response?.data?.message ||
          "Não foi possível abrir o texto para anotação."
      );
    }
  }
}

async function fetchTextsPaged() {
  isLoading.value = true;
  try {
    await textStore.fetchTextsPaged(
      currentPage.value,
      pageSize.value,
      textStore.searchTerm
    );
  } catch (error) {
    console.error("❌ Erro ao buscar textos paginados:", error.message);
    showErrorToast("Falha ao carregar a lista de textos.");
  } finally {
    isLoading.value = false;
  }
}

watch(currentPage, fetchTextsPaged);

// ─────────────────────────────────────────────────────────────────────────────
// FILTROS — mesmo padrão de NoteGeneralForm.vue / ActivityGeneralForm.vue
// ─────────────────────────────────────────────────────────────────────────────
const local = reactive({
  title: textStore.filters.title ?? "",
  authorName: textStore.filters.authorName ?? "",
  subtitle: textStore.filters.subtitle ?? "",
  source: textStore.filters.source ?? "",
  categoryName: textStore.filters.categoryName ?? "",
  topicName: textStore.filters.topicName ?? "",
  creatorName: textStore.filters.creatorName ?? "",
  creatorId: textStore.filters.creatorId ?? "",
  textId: textStore.filters.textId ?? "",
  createdFrom: textStore.filters.createdFrom ?? "",
  createdTo: textStore.filters.createdTo ?? "",
  permissionGroupName: textStore.filters.permissionGroupName ?? "",
  permissionUserName: textStore.filters.permissionUserName ?? "",
  // espelha myTextsOnly da store — vira chip como qualquer outra condição
  myTextsOnly: textStore.myTextsOnly ?? false,
});

const debouncedNumeric = debounce((field, value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed !== "" && (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0)) return;
  textStore.setFilter(field, trimmed);
}, 400);

const debouncedText = debounce((field, value) => {
  textStore.setFilter(field, String(value ?? "").trim());
}, 300);

watch(
  () => local.title,
  (v) => debouncedText("title", v)
);
watch(
  () => local.authorName,
  (v) => debouncedText("authorName", v)
);
watch(
  () => local.subtitle,
  (v) => debouncedText("subtitle", v)
);
watch(
  () => local.source,
  (v) => debouncedText("source", v)
);
watch(
  () => local.categoryName,
  (v) => debouncedText("categoryName", v)
);
watch(
  () => local.topicName,
  (v) => debouncedText("topicName", v)
);
watch(
  () => local.creatorName,
  (v) => debouncedText("creatorName", v)
);
watch(
  () => local.creatorId,
  (v) => debouncedNumeric("creatorId", v)
);
watch(
  () => local.textId,
  (v) => debouncedNumeric("textId", v)
);
watch(
  () => local.createdFrom,
  (v) => debouncedText("createdFrom", v)
);
watch(
  () => local.createdTo,
  (v) => debouncedText("createdTo", v)
);
watch(
  () => local.permissionGroupName,
  (v) => debouncedText("permissionGroupName", v)
);
watch(
  () => local.permissionUserName,
  (v) => debouncedText("permissionUserName", v)
);
watch(
  () => local.myTextsOnly,
  (v) => textStore.setMyTextsOnly(v)
);

watch(
  () => [{ ...textStore.filters }, textStore.myTextsOnly],
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchTextsPaged();
    }
  },
  { deep: true }
);

// `short` + `verb` compõem o rótulo da chip (ex.: Autor contém "Camões").
const ADVANCED_FILTERS = [
  {
    key: "subtitle",
    label: "Subtítulo",
    type: "text",
    placeholder: "ex: canto I…",
    short: "Subtítulo",
    verb: "contém",
    icon: "fas fa-align-left",
  },
  {
    key: "source",
    label: "Fonte",
    type: "text",
    placeholder: "ex: domínio público…",
    short: "Fonte",
    verb: "contém",
    icon: "fas fa-link",
  },
  {
    key: "creatorName",
    label: "Criador",
    type: "text",
    placeholder: "ex: Maria…",
    short: "Criador",
    verb: "=",
    icon: "fas fa-user",
  },
  {
    key: "creatorId",
    label: "ID do criador",
    type: "numeric",
    placeholder: "ex: 7",
    short: "ID do criador",
    verb: "=",
    icon: "fas fa-id-badge",
  },
  {
    key: "createdRange",
    label: "Data de criação",
    type: "dateRange",
    fromKey: "createdFrom",
    toKey: "createdTo",
    short: "Data",
    icon: "fas fa-calendar-alt",
  },
  // Fecha a primeira coluna do menu "Adicionar filtro"
  {
    key: "categoryName",
    label: "Gênero",
    type: "text",
    placeholder: "ex: Épica…",
    short: "Gênero",
    verb: "contém",
    icon: "fas fa-book-open",
  },
  {
    key: "topicName",
    label: "Assunto",
    type: "text",
    placeholder: "ex: Tempos Verbais…",
    short: "Assunto",
    verb: "contém",
    icon: "fas fa-lightbulb",
  },
  {
    key: "textId",
    label: "ID do texto",
    type: "numeric",
    placeholder: "ex: 55",
    short: "ID do texto",
    verb: "=",
    icon: "fas fa-fingerprint",
  },
  {
    key: "permissionGroupName",
    label: "Compartilhado com grupo",
    type: "text",
    placeholder: "ex: Crianças…",
    short: "Compartilhado com grupo",
    verb: "quoted",
    icon: "fas fa-users-cog",
  },
  {
    key: "permissionUserName",
    label: "Compartilhado com usuário",
    type: "text",
    placeholder: "ex: Maria…",
    short: "Compartilhado com usuário",
    verb: "quoted",
    icon: "fas fa-user-tag",
  },
];

// Filtros da linha principal — não entram no dropdown, mas viram chip no painel.
const BASE_FILTERS = [
  {
    key: "title",
    type: "text",
    short: "Título",
    verb: "contém",
    icon: "fas fa-heading",
  },
  {
    key: "authorName",
    type: "text",
    short: "Autor",
    verb: "contém",
    icon: "fas fa-feather-alt",
  },
  // Botão "Meus textos" — vira chip como qualquer outra condição.
  {
    key: "myTextsOnly",
    type: "checkbox",
    short: "Meus textos",
    icon: "fas fa-user",
  },
];
const ALL_FILTERS = [...BASE_FILTERS, ...ADVANCED_FILTERS];

const isAdvancedPanelOpen = ref(false);
const isAddFilterOpen = ref(false);
const addFilterMenuRef = ref(null);
const activeFilterKeys = ref([]);
const addedInThisSession = ref([]);

// Duas colunas independentes: abrir um campo cresce só a coluna dele.
const addFilterColumns = computed(() => {
  const options = addFilterOptions.value;
  const half = Math.ceil(options.length / 2);
  return [options.slice(0, half), options.slice(half)];
});

const activeFilters = computed(() =>
  activeFilterKeys.value
    .map((key) => ADVANCED_FILTERS.find((f) => f.key === key))
    .filter(Boolean)
);

// Filtros ainda não adicionados — é o que o dropdown mostra ao ser aberto.
const remainingFilters = computed(() =>
  ADVANCED_FILTERS.filter((f) => !activeFilterKeys.value.includes(f.key))
);

// Um filtro adicionado continua listado enquanto o dropdown permanecer aberto,
// para que o campo de preenchimento fique visível abaixo do seu nome.
const addFilterOptions = computed(() =>
  ADVANCED_FILTERS.filter(
    (f) =>
      !activeFilterKeys.value.includes(f.key) ||
      addedInThisSession.value.includes(f.key)
  )
);

function isFilterAdded(key) {
  return activeFilterKeys.value.includes(key);
}

// Um filtro só vira chip quando tem valor — opção clicada e não preenchida não conta.
function hasFilterValue(filter) {
  if (!filter) return false;
  if (filter.type === "checkbox") return local[filter.key] === true;
  if (filter.type === "dateRange")
    return Boolean(local[filter.fromKey] || local[filter.toKey]);
  return String(local[filter.key] ?? "").trim() !== "";
}

const chipFilters = computed(() => [
  ...BASE_FILTERS.filter(hasFilterValue),
  ...activeFilters.value.filter(hasFilterValue),
]);

// Filtros com controle próprio à vista (campos da linha principal e o botão
// "Meus textos"): sozinhos, não abrem o painel.
const PRIMARY_KEYS = ["title", "authorName", "myTextsOnly"];
const panelOnlyChips = computed(() =>
  chipFilters.value.filter((f) => !PRIMARY_KEYS.includes(f.key))
);

const isPanelVisible = computed(
  () => isAdvancedPanelOpen.value || panelOnlyChips.value.length > 0
);

// "Meus textos" tem o próprio botão para desligar — sozinho, não justifica o Limpar.
const hasActiveFilters = computed(() =>
  Object.entries(local).some(([key, v]) => {
    if (key === "myTextsOnly") return false;
    return typeof v === "boolean" ? v === true : String(v ?? "").trim() !== "";
  })
);

// Descarta as opções abertas no dropdown que ficaram sem preenchimento.
function pruneEmptyFilters() {
  activeFilterKeys.value = activeFilterKeys.value.filter((key) =>
    hasFilterValue(ADVANCED_FILTERS.find((f) => f.key === key))
  );
  addedInThisSession.value = [];
}

function closeAddFilterDropdown() {
  isAddFilterOpen.value = false;
  pruneEmptyFilters();
}

function toggleAdvancedPanel() {
  isAdvancedPanelOpen.value = !isAdvancedPanelOpen.value;
  if (!isAdvancedPanelOpen.value) closeAddFilterDropdown();
}

// O menu pode abrir mesmo sem filtros restantes: opções com chip reabrem em edição.
const hasAddFilterContent = computed(
  () => remainingFilters.value.length > 0 || panelOnlyChips.value.length > 0
);

function toggleAddFilterDropdown() {
  if (isAddFilterOpen.value) {
    closeAddFilterDropdown();
    return;
  }
  if (!hasAddFilterContent.value) return;
  isAddFilterOpen.value = true;
  // Opções que já têm chip entram listadas e expandidas, exibindo seus valores.
  addedInThisSession.value = activeFilterKeys.value.filter((key) =>
    hasFilterValue(ADVANCED_FILTERS.find((f) => f.key === key))
  );
  // Ao abrir, a primeira opção disponível já entra selecionada em modo de edição.
  const first = addFilterOptions.value[0];
  if (first) selectFilterOption(first.key);
}

// Seleção (via setas ou abertura do menu) já coloca o campo em modo de edição:
// expande a opção e foca o input, sem precisar de Enter. Checkbox não expande —
// marcar/desmarcar fica no Espaço para não ativar o filtro só de passagem.
function selectFilterOption(key) {
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!def) return;
  if (def.type !== "checkbox" && !activeFilterKeys.value.includes(key)) {
    activeFilterKeys.value.push(key);
    addedInThisSession.value.push(key);
  }
  nextTick(() => {
    const menu = addFilterMenuRef.value;
    if (!menu) return;
    const option = menu.querySelector(`[data-filter-key="${CSS.escape(key)}"]`);
    if (!option) return;
    const target =
      def.type === "checkbox"
        ? option.querySelector(".add-filter-name")
        : option.querySelector('input:not([type="checkbox"])') ||
          option.querySelector(".add-filter-name");
    target?.focus();
  });
}

// Ao navegar para fora, opção expandida que ficou vazia volta a recolher.
function collapseFilterIfEmpty(key) {
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!def || def.type === "checkbox" || hasFilterValue(def)) return;
  activeFilterKeys.value = activeFilterKeys.value.filter((k) => k !== key);
  addedInThisSession.value = addedInThisSession.value.filter((k) => k !== key);
}

// Setas movem a seleção entre as opções: cima/baixo percorrem a coluna (com
// volta); esquerda/direita alternam entre as colunas mantendo a linha.
// Em campos já preenchidos, ←/→ continuam movendo o cursor do input.
function handleAddFilterMenuKeydown(event) {
  // Tab não circula entre as opções — a navegação é pelas setas.
  if (event.key === "Tab") {
    event.preventDefault();
    return;
  }
  // Enter fecha o menu; os valores preenchidos viram chips.
  if (event.key === "Enter") {
    event.preventDefault();
    closeAddFilterDropdown();
    return;
  }
  if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key))
    return;

  const optionEl = event.target.closest("[data-filter-key]");
  if (!optionEl) return;
  const currentKey = optionEl.dataset.filterKey;

  const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
  const isTextInput =
    event.target.matches("input") && event.target.type !== "checkbox";
  if (horizontal && isTextInput && event.target.value !== "") return;

  const columns = addFilterColumns.value;
  let col = columns.findIndex((c) => c.some((f) => f.key === currentKey));
  if (col === -1) return;
  let row = columns[col].findIndex((f) => f.key === currentKey);

  if (event.key === "ArrowDown") {
    row = (row + 1) % columns[col].length;
  } else if (event.key === "ArrowUp") {
    row = (row - 1 + columns[col].length) % columns[col].length;
  } else {
    const nextCol = (col + 1) % columns.length;
    if (!columns[nextCol]?.length) return;
    col = nextCol;
    row = Math.min(row, columns[col].length - 1);
  }

  event.preventDefault();
  collapseFilterIfEmpty(currentKey);
  selectFilterOption(columns[col][row].key);
}

function addFilter(key) {
  if (activeFilterKeys.value.includes(key)) return;
  activeFilterKeys.value.push(key);
  addedInThisSession.value.push(key);
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (def?.type === "checkbox") local[key] = true;
  focusAddedFilterInput(key);
}

// Ao adicionar um filtro pelo menu "Adicionar filtro", foca automaticamente o
// respectivo campo (após o v-if renderizar o input) para o usuário já digitar.
// Checkbox não tem campo de texto, então é ignorado.
function focusAddedFilterInput(key) {
  nextTick(() => {
    const menu = addFilterMenuRef.value;
    if (!menu) return;
    const option = menu.querySelector(`[data-filter-key="${CSS.escape(key)}"]`);
    const input = option?.querySelector('input:not([type="checkbox"])');
    if (input) input.focus();
  });
}

// Clique na opção: abre o campo; se já estiver aberto e sem preenchimento, recolhe.
function toggleFilterOption(key) {
  if (!activeFilterKeys.value.includes(key)) {
    addFilter(key);
    return;
  }
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!hasFilterValue(def)) removeFilter(key);
}

function removeFilter(key) {
  const def = ALL_FILTERS.find((f) => f.key === key);
  if (def?.type === "dateRange") {
    local[def.fromKey] = "";
    local[def.toKey] = "";
  } else if (def?.type === "checkbox") {
    local[key] = false;
  } else if (def) {
    local[key] = "";
  }
  activeFilterKeys.value = activeFilterKeys.value.filter((k) => k !== key);
  addedInThisSession.value = addedInThisSession.value.filter((k) => k !== key);
}

function clearAllFilters() {
  Object.keys(local).forEach((k) => {
    local[k] = typeof local[k] === "boolean" ? false : "";
  });
  activeFilterKeys.value = [];
  addedInThisSession.value = [];
  isAddFilterOpen.value = false;
  textStore.clearFilters();
}

function toggleMyTexts() {
  local.myTextsOnly = !local.myTextsOnly;
  if (currentPage.value !== 1) currentPage.value = 1;
}

function formatChipDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const [year, month, day] = raw.split("-");
  return day && month && year ? `${day}/${month}/${year.slice(-2)}` : raw;
}

// Rótulo da chip — curto e semântico. Os campos de preenchimento vivem no dropdown.
function chipLabel(filter) {
  if (filter.type === "checkbox") return filter.short;

  if (filter.type === "dateRange") {
    const from = formatChipDate(local[filter.fromKey]);
    const to = formatChipDate(local[filter.toKey]);
    if (from && to) return `${filter.short}: ${from} → ${to}`;
    if (from) return `${filter.short} ≥ ${from}`;
    if (to) return `${filter.short} ≤ ${to}`;
    return `${filter.short}…`;
  }

  const value = String(local[filter.key] ?? "").trim();
  if (!value) return `${filter.short}…`;
  // "quoted": o próprio rótulo já diz a relação (ex.: Compartilhado com grupo "Crianças")
  if (filter.verb === "quoted") return `${filter.short} "${value}"`;
  return filter.verb === "="
    ? `${filter.short} = ${value}`
    : `${filter.short} contém "${value}"`;
}

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }

  if (
    isAddFilterOpen.value &&
    !event.target.closest(".add-filter-dropdown-anchor")
  )
    closeAddFilterDropdown();

  // Foco saiu do botão "Mais filtros" e do painel sem nenhum filtro
  // efetivamente marcado → o botão volta ao estado desmarcado.
  if (
    isAdvancedPanelOpen.value &&
    !event.target.closest(".advanced-filters-anchor") &&
    !event.target.closest(".active-filters-panel") &&
    panelOnlyChips.value.length === 0
  ) {
    isAdvancedPanelOpen.value = false;
    closeAddFilterDropdown();
  }
}

// Primeiro filtro da barra. A tela abre pronta para filtrar: o cursor entra
// nele sozinho, sem o usuário ter de ir ao campo com o mouse.
const titleInputRef = ref(null);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  nextTick(() => titleInputRef.value?.focus());
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  resetTextSearchState();
});

// Item em aberto clicado no fim do cadastro rápido: o texto já existe, e
// quem quer catalogá-lo agora segue direto para a edição dele. Se o clique veio
// de um item em aberto ("Autor", "Gênero", "Assuntos"), o campo vai junto para
// a edição abrir com o cursor nele.
const editFocusField = ref(null);

function closeEditModal() {
  showEditModal.value = false;
  editFocusField.value = null;
}

async function handleCompleteTextDetails(payload) {
  const textId = typeof payload === "object" ? payload?.textId : payload;
  editFocusField.value =
    (typeof payload === "object" ? payload?.focus : null) ?? null;

  showAddModal.value = false;
  textStore.setIdText(textId);

  await handleTextsChanged({
    goToFirstPage: true,
    closeAdd: true,
    closeEdit: false,
  });

  // O EditTextForm carrega o texto por conta própria (getTextById): aqui basta
  // deixar o id selecionado.
  showEditModal.value = true;
}

function indexSelector(index) {
  const text = textStore.texts[index];
  textStore.setIdText(text.textId);
  textStore.selectedText = text;
}

// ── Guard de atividades aplicado no clique da linha ──────────────────────────
// O clique da linha leva às atividades, então carrega os mesmos guards que
// antes ficavam no botão: o portão do vínculo (memberCanViewActivities) e a
// permissão por texto. Como uma linha não tem estado "desabilitado com
// tooltip", canOpenActivities também governa a classe .clickable-row — sem
// permissão, a linha nem exibe o cursor de mão, para o clique morto não
// parecer defeito.
function canOpenActivities(text) {
  if (!memberCanViewActivities.value) return false;
  return (
    (hasAnyPermission(text, "selecionar", "editar") && text?.active) ||
    userStore.currentUser?.userId == text?.createdByUserId
  );
}

function handleRowClick(index) {
  const text = textStore.texts[index];

  // Sem "selecionar"/"editar" o texto só pode ser lido na ficha. O clique
  // costumava morrer em silêncio, e quem não sabia da regra achava que era
  // defeito — daí o retorno explícito.
  if (!canOpenActivities(text)) {
    showErrorToast(
      "Você tem apenas permissão de visualizar este texto. Para anotar nele, " +
        "peça permissão de seleção ao responsável."
    );
    return;
  }

  indexSelector(index);
  goToActivities(text.textId);
}

// Um texto do cadastro rápido pode nascer sem autor, gênero ou assunto: a
// célula vazia leria como falha de carregamento, o travessão lê como "ainda
// não preenchido".
function catalogCell(value) {
  return toTitleCase(value) || "—";
}

function getPrimaryTopicName(text) {
  if (!text || !Array.isArray(text.topicNames) || text.topicNames.length === 0)
    return "";
  return text.topicNames[0] || "";
}

function hasAnyPermission(text, ...permissionNames) {
  if (!text || !Array.isArray(text.userPermissions)) return false;
  const lowerNames = permissionNames.map((p) => p.toLowerCase());
  return text.userPermissions.some((perm) =>
    lowerNames.includes(perm.permissionName?.toLowerCase())
  );
}

// ── NOVO: guards combinam vínculo (portão) AND grupo (filtro interno) ─────────
function canEdit(text) {
  return memberCanEditText.value && hasAnyPermission(text, "Editar");
}
function canView(text) {
  return memberCanViewTexts.value && hasAnyPermission(text, "Visualizar");
}
function canSelect(text) {
  return memberCanViewTexts.value && hasAnyPermission(text, "Selecionar");
}
function isTextCreator(text) {
  return userStore.currentUser?.userId === text?.createdByUserId;
}

async function handleStatusToggle(text) {
  try {
    await textStore.updateTextStatus({
      textId: text.textId,
      active: !text.active,
    });
    showSuccessToast(
      `Status do texto "${toTitleCase(text.title)}" alterado para ${
        text.active ? "inativo" : "ativo"
      } com sucesso!`
    );
  } catch (error) {
    console.error("❌ Falha ao atualizar o status do texto:", error);
    showErrorToast(
      error?.response?.data?.message ||
        error?.message ||
        "Ocorreu um erro ao atualizar o status."
    );
  }
}

async function handleTextsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;
  if (clearSelection) textStore.selectedTextIds = [];

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchTextsPaged();
    return;
  }
  await fetchTextsPaged();
}

function isSysUserName(name) {
  return (name ?? "").trim().toLowerCase() === "sys";
}

onMounted(async () => {
  // Liga o loading já de cara — evita mostrar textStore.texts desatualizado
  // (de uma visita anterior) enquanto os fetches auxiliares abaixo ainda rodam.
  isLoading.value = true;
  await authorStore.fetchAuthors();
  await categoryStore.fetchCategories();
  await topicStore.fetchTopics();
  await fetchTextsPaged();
});
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Pesquisa por Textos</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie textos criados por você ou compartilhados com você.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <!-- Área rolável: cabeçalho fica fixo; filtros, tabela e paginador rolam -->
      <div class="scroll-area">
        <div class="action-bar action-bar-stacked">
        <div class="filter-panel w-full">
          <div class="filter-row">
            <!-- Título -->
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Título</label>
              <input
                ref="titleInputRef"
                v-model="local.title"
                type="text"
                placeholder="ex: Dom Quixote…"
                class="filter-input"
              />
            </div>

            <!-- Autor -->
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Autor</label>
              <input
                v-model="local.authorName"
                type="text"
                placeholder="ex: Camões…"
                class="filter-input"
              />
            </div>

            <div class="filter-actions-right">
              <div
                class="filter-field filter-field-batch-actions advanced-filters-anchor"
              >
                <label class="filter-label">&nbsp;</label>
                <button
                  type="button"
                  class="btn btn-batch btn-advanced"
                  :class="{ active: isPanelVisible }"
                  title="Exibir mais filtros"
                  @click.stop="toggleAdvancedPanel"
                >
                  <i class="fas fa-filter"></i>
                  <span>Mais filtros</span>
                  <span v-if="chipFilters.length" class="advanced-count">{{
                    chipFilters.length
                  }}</span>
                  <svg
                    class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                    :class="{ open: isAdvancedPanelOpen }"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      clip-rule="evenodd"
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    />
                  </svg>
                </button>
              </div>

              <div
                v-if="hasActiveFilters"
                class="filter-field filter-field-clear"
              >
                <label class="filter-label">&nbsp;</label>
                <button
                  class="filter-clear"
                  title="Limpar filtros"
                  @click="clearAllFilters"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="clear-icon"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>Limpar</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Painel de filtros ativos -->
          <div v-if="isPanelVisible" class="active-filters-panel">
            <div class="active-filters-title">
              <i class="fas fa-filter"></i>
              <span>Filtros ativos</span>
            </div>

            <div v-if="chipFilters.length" class="active-filters-chips">
              <div
                v-for="filter in chipFilters"
                :key="filter.key"
                class="filter-chip"
                :title="chipLabel(filter)"
              >
                <i :class="filter.icon" class="filter-chip-icon"></i>
                <span class="filter-chip-label">{{ chipLabel(filter) }}</span>
                <button
                  type="button"
                  class="filter-chip-remove"
                  :title="`Remover filtro ${filter.short}`"
                  @click="removeFilter(filter.key)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <p v-else class="active-filters-empty">Nenhum filtro adicionado.</p>

            <div
              class="add-filter-dropdown-anchor"
              style="position: relative; align-self: flex-start"
            >
              <button
                type="button"
                class="btn btn-batch btn-add-filter"
                :disabled="!hasAddFilterContent"
                @click.stop="toggleAddFilterDropdown"
              >
                <i class="fas fa-plus"></i>
                <span>Adicionar filtro</span>
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isAddFilterOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div
                v-if="isAddFilterOpen && addFilterOptions.length > 0"
                ref="addFilterMenuRef"
                class="dropdown-menu add-filter-menu"
                @keydown="handleAddFilterMenuKeydown"
              >
                <div class="add-filter-grid">
                  <div
                    v-for="(column, columnIndex) in addFilterColumns"
                    :key="`add-filter-col-${columnIndex}`"
                    class="add-filter-column"
                  >
                    <div
                      v-for="filter in column"
                      :key="filter.key"
                      :data-filter-key="filter.key"
                      class="add-filter-option"
                      :class="{
 'is-open': isFilterAdded(filter.key),
                        'is-checkbox': filter.type === 'checkbox',
                      }"
                      @click.stop="toggleFilterOption(filter.key)"
                    >
                      <button
                        type="button"
                        class="add-filter-name"
                        @click.stop="toggleFilterOption(filter.key)"
                      >
                        <i :class="filter.icon" class="add-filter-icon"></i>
                        <span>{{ filter.label }}</span>
                      </button>

                      <template v-if="isFilterAdded(filter.key)">
                        <template v-if="filter.type === 'dateRange'">
                          <div class="add-filter-inputs">
                            <span class="add-filter-sublabel">de</span>
                            <input
                              v-model="local[filter.fromKey]"
                              type="date"
                              class="add-filter-input add-filter-input-date"
                              @click.stop
                            />
                            <span class="add-filter-sublabel">até</span>
                            <input
                              v-model="local[filter.toKey]"
                              type="date"
                              class="add-filter-input add-filter-input-date"
                              @click.stop
                            />
                          </div>
                        </template>

                        <template v-else>
                          <input
                            v-model="local[filter.key]"
                            type="text"
                            :inputmode="
                              filter.type === 'numeric' ? 'numeric' : undefined
                            "
                            :placeholder="filter.placeholder"
                            class="add-filter-input"
                            @click.stop
                          />
                        </template>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="filter-secondary-row">
            <!-- Sem "Adicionar Texto" aqui: criar texto é ação da estante
                 (TextCardsForm), no botão flutuante. Esta tela é a de busca. -->

            <!-- Meus textos -->
            <button
              type="button"
              class="btn btn-batch btn-my-texts"
              :class="{ active: local.myTextsOnly }"
              title="Filtrar apenas meus textos"
              @click="toggleMyTexts"
            >
              <i class="fas fa-user"></i>
              <span>Meus textos</span>
            </button>

            <div ref="batchDropdownRef" class="relative">
              <button
                @click="isBatchDropdownOpen = !isBatchDropdownOpen"
                class="btn btn-batch"
                :disabled="!canBatchActions"
              >
                Ações em lote
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isBatchDropdownOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div v-if="isBatchDropdownOpen" class="dropdown-menu">
                <div class="py-1">
                  <!-- ── NOVO: Apagar em lote condicionado ao guard de delete ── -->
                  <a
                    v-if="memberCanDeleteText"
                    href="#"
                    role="button"
                    @click.prevent="tryOpenBatchDeleteModal"
                    class="dropdown-item danger-item"
                  >
                    <i class="fas fa-trash-alt"></i>
                    <span>Apagar selecionados</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 5.5%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 100px">Ações</th>
              <th style="width: 6%">Id</th>
              <th style="width: 25%">Título</th>
              <th style="width: 22%">Autor</th>
              <th style="width: 16%">Gênero</th>
              <th style="width: 10%">Assuntos</th>
              <th style="width: 14%">Criador</th>
              <th style="width: 18%">Criação</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(text, index) in !isLoading ? textStore.texts : []"
              :key="text.textId"
              @click="handleRowClick(index)"
              :class="{ 'clickable-row': canOpenActivities(text) }"
              :title="
                canOpenActivities(text)
                  ? null
                  : 'Sem permissão para acessar as atividades deste texto'
              "
            >
              <td @click.stop>
                <input
                  :class="{ 'disabled-item': !canEdit(text) }"
                  type="checkbox"
                  :value="text.textId"
                  :checked="textStore.selectedTextIds?.includes(text.textId)"
                  @change="toggleSelection(text.textId)"
                  class="checkbox-style"
                  :disabled="!canEdit(text)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <div class="row-actions">
                  <button
                    v-if="hasAnyPermission(text, 'visualizar', 'selecionar')"
                    @click="memberCanViewTexts && handleView(index)"
                    class="btn-icon btn-slate"
                    :class="{ 'btn-icon-disabled': !memberCanViewTexts }"
                    :title="
                      memberCanViewTexts
                        ? 'Detalhes do texto'
                        : 'Sem permissão para visualizar textos neste vínculo'
                    "
                    :aria-disabled="!memberCanViewTexts"
                  >
                    <i class="fas fa-info-circle fa-lg"></i>
                  </button>

                  <button
                    @click="toggleRowDropdown(text.textId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === text.textId"
                  class="row-dropdown-menu"
                  :class="{
 'opens-up': rowMenuPlacement.up,
                    'opens-left': rowMenuPlacement.left,
                  }"
                  :style="{
                    maxHeight: rowMenuPlacement.maxHeight
                      ? rowMenuPlacement.maxHeight + 'px'
                      : null,
                    visibility: rowMenuPlacement.ready ? 'visible' : 'hidden',
                  }"
                >
                  <ul>
                    <li
                      :class="{
 'disabled-item': !(canView(text) || canSelect(text)),
                      }"
                      @click="
                        (canView(text) || canSelect(text)) && handleView(index)
                      "
                    >
                      <i class="fas fa-info-circle"></i
                      ><span>Detalhes do texto</span>
                    </li>

                    <li
                      v-if="isTextCreator(text)"
                      @click="handleViewPermissions(index)"
                    >
                      <i class="fas fa-user-shield"></i
                      ><span>Ver permissões</span>
                    </li>

                    <!-- ── NOVO: Editar condicionado ao guard de edit ──────── -->
                    <li
                      v-if="memberCanEditText"
                      :class="{ 'disabled-item': !canEdit(text) }"
                      @click="canEdit(text) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <!-- ── NOVO: Apagar condicionado ao guard de delete ────── -->
                    <li
                      v-if="memberCanDeleteText"
                      :class="{
 'disabled-item': !canEdit(text),
                        'danger-item': true,
                      }"
                      @click="canEdit(text) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>

                  <div class="dropdown-divider"></div>

                  <div class="dropdown-toggle-item">
                    <span class="label-text">Ativo</span>
                    <label class="toggle-switch">
                      <input
                        type="checkbox"
                        :checked="text.active"
                        :disabled="!canEdit(text)"
                        @change="handleStatusToggle(text)"
                      />
                      <span class="slider"></span>
                    </label>
                  </div>
                </div>
              </td>

              <td>
                <span :title="text.textId" style="font-size: smaller">{{
                  text.textId
                }}</span>
              </td>

              <td class="cell-text-left">
                <span :title="text.title">{{ toTitleCase(text.title) }}</span>
              </td>

              <td class="cell-text-left">
                <span
                  :title="text.authorName || 'Autor ainda não informado'"
                  style="font-size: smaller"
                  >{{ catalogCell(text.authorName) }}</span
                >
              </td>

              <td class="cell-text-left">
                <span
                  :title="text.categoryName || 'Gênero ainda não informado'"
                  style="font-size: smaller"
                  >{{ catalogCell(text.categoryName) }}</span
                >
              </td>

              <td>
                <span
                  style="font-size: smaller"
                  :title="
                    getPrimaryTopicName(text) || 'Assunto ainda não informado'
                  "
                >
                  {{ catalogCell(getPrimaryTopicName(text)) }}
                </span>
              </td>

              <td>
                <span
                  v-if="isSysUserName(text.createdByUserName)"
                  class="sys-pill"
                  title="Criado pelo Sys"
                  >SYS</span
                >
                <span
                  v-else
                  :title="text.createdByUserName"
                  style="font-size: smaller"
                  >{{ text.createdByUserName }}</span
                >
              </td>

              <td>
                <span
                  :title="formatDateTimeForDisplay(text.createdAt)"
                  style="font-size: smaller"
                >
                  {{ formatDateTimeForDisplay(text.createdAt) }}
                </span>
              </td>
            </tr>
            <tr v-if="isLoading">
              <td colspan="9" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>

                  <p>Carregando textos...</p>
                </div>
              </td>
            </tr>

            <tr v-else-if="textStore.texts.length === 0">
              <td colspan="9" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <template v-if="isOnboardingPending">
                    <i class="fas fa-pen-to-square empty-icon empty-icon--invite"></i>
                    <p>Você ainda não tem textos cadastrados.</p>
                    <!-- Aponta para a estante: é lá que o botão de criar vive
                         agora, e mandar clicar num botão que não existe nesta
                         tela seria pior que não dizer nada. -->
                    <p class="empty-hint">Use o botão + na tela Textos para começar!</p>
                  </template>
                  <template v-else>
                    <img
                      src="/img/estante-sem-resultado.webp"
                      alt=""
                      width="320"
                      height="206"
                      class="empty-illustration"
                    />
                    <p>Nenhum texto encontrado.</p>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoading && textStore.totalCount > 0"
          class="text-pagination-row"
        >
          <div class="text-pagination-left">
            <span>Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="textStore.totalCount"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="textStore.totalCount"
            :total-pages="textStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
        </div>
        <!-- fim table-wrapper -->
      </div>
      <!-- fim scroll-area -->
    </div>
  </div>

  <!-- Modais -->
  <Teleport to="body">
    <!-- ── NOVO: guards de vínculo nos modais ─────────────────────────────── -->
    <AddTextForm
      v-if="showAddModal && memberCanCreateText"
      @close="showAddModal = false"
      @text-added="handleTextsChanged({ goToFirstPage: true, closeAdd: true })"
      @complete-details="handleCompleteTextDetails"
    />

    <EditTextForm
      v-if="showEditModal && memberCanEditText"
      :textId="textStore.idText"
      :focusField="editFocusField"
      @close="closeEditModal"
      @text-updated="
        handleTextsChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <DeleteTextForm
      v-if="showDeleteModal && memberCanDeleteText"
      :text="textStore.selectedText"
      @close="showDeleteModal = false"
      @notify="handleNotify"
      @text-deleted="
        handleTextsChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
    />

    <BatchDeleteTextForm
      v-if="showBatchDeleteModal && memberCanDeleteText"
      @close="showBatchDeleteModal = false"
      @notify="handleNotify"
      @texts-deleted="
        handleTextsChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
    />

    <TextCardModal
      v-if="showTextCardModal"
      :show="showTextCardModal"
      :text-id="selectedTextCardId"
      @close="showTextCardModal = false"
      @text-updated="handleCardTextUpdated"
      @notify="handleNotify"
    />

    <PermissionView
      v-if="showPermissionModal"
      :show="showPermissionModal"
      entity-type="text"
      :text-id="selectedPermissionTextId"
      title="Permissões do texto"
      @close="showPermissionModal = false"
    />

    <TextHelpModal :show="showHelpModal" @close="showHelpModal = false" />
  </Teleport>

  <!-- Toast -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.main-header {
  flex-shrink: 0;
}

/* Rola tudo abaixo do cabeçalho (que fica fixo): filtros, tabela e paginador */
.scroll-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

/* ── FILTROS — mesmo padrão de NoteGeneralForm.vue / ActivityGeneralForm.vue ── */
.action-bar-stacked {
  flex-direction: column !important;
  align-items: stretch !important;
}
.filter-panel {
  width: 100%;
  flex-shrink: 0;
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 90px;
  min-width: 80px;
  max-width: 160px;
}
/* Título e Autor crescem para preencher toda a largura livre da linha */
.filter-field-wide {
  flex: 1 1 0;
  min-width: 130px;
  max-width: none;
}
.filter-field-clear,
.filter-field-batch-actions {
  flex: 0 0 auto;
  min-width: unset;
  max-width: unset;
}
.filter-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-light);
  white-space: nowrap;
  padding-left: 2px;
}
.filter-input {
  width: 100%;
  height: 32px;
  padding: 0.3rem 0.55rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.8rem;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}
.filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
/* Mais filtros / Limpar ficam colados à direita do campo Autor */
.filter-actions-right {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex: 0 0 auto;
  min-width: max-content;
}
/* Adicionar Texto / Ações em lote — abaixo do painel, à direita */
.filter-secondary-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: auto;
  height: 32px;
  padding: 0 0.65rem;
  flex-shrink: 0;
  border: 1px solid var(--danger);
  border-radius: 10px;
  background-color: var(--danger-bg);
  color: var(--danger-text);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
  white-space: nowrap;
}
/* mesmo comportamento de clique do botão "Mais filtros" (.btn-batch) */
.filter-clear:hover {
  background-color: var(--danger-bg);
}
.filter-clear:active {
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  transform: translateY(1px);
}
.clear-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}
.btn-advanced.active,
.btn-my-texts.active {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
  color: var(--text-on-brand);
}
.btn-advanced.active:hover,
.btn-my-texts.active:hover {
  background-color: var(--brand-light-hover);
}
.advanced-count {
  display: inline-grid;
  place-items: center;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 999px;
  background-color: var(--text-light);
  color: var(--surface);
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}
/* Painel "Filtros ativos" — paleta do card de metadados de TextDisplayer.vue */
.active-filters-panel {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 0.75rem;
  padding: 14px 18px 16px;
  border: 1px solid rgba(202, 138, 4, 0.22);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.32);
  box-shadow: 0 2px 8px rgba(120, 53, 15, 0.06);
}
.active-filters-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-dark);
}
.active-filters-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.active-filters-empty {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-light);
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 32px;
  padding: 0 0.35rem 0 0.6rem;
  border: 1px solid #ded5ff;
  /* mesmo raio do botão "Mais filtros" (.btn-batch) */
  border-radius: 10px;
  background-color: #ece7ff;
  color: #4b3f86;
}
.filter-chip-icon {
  width: 13px;
  text-align: center;
  font-size: 0.72rem;
  color: inherit;
  opacity: 0.75;
  flex-shrink: 0;
}
.filter-chip-label {
  font-size: 0.76rem;
  font-weight: 600;
  color: inherit;
  white-space: nowrap;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: inherit;
  opacity: 0.65;
  font-size: 0.7rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}
.filter-chip-remove:hover {
  background-color: var(--danger-bg);
  color: var(--danger-text);
  opacity: 1;
}
.btn-add-filter {
  gap: 0.4rem;
}
.add-filter-menu {
  right: auto;
  left: 0;
  /* alto o bastante para caber a coluna inteira com campos abertos, sem rolagem */
  max-height: min(560px, calc(100vh - 160px));
  overflow-y: auto;
  padding: 0.4rem;
}
/* duas colunas independentes: abrir um campo cresce só a coluna dele */
.add-filter-grid {
  display: flex;
  align-items: stretch;
}
.add-filter-column {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 330px;
  flex: 0 0 330px;
}
.add-filter-column + .add-filter-column {
  border-left: 1px solid var(--border-color);
}
/* o campo abre sempre na linha de baixo, ocupando a largura da coluna */
.add-filter-option {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.3rem;
  padding: 0.25rem 0.7rem;
  border-radius: 8px;
  min-width: 0;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.add-filter-option:hover {
  background-color: #f5f3ff;
}
/* foco (navegação por setas) espelha o hover na linha inteira */
.add-filter-option:focus-within {
  background-color: #f5f3ff;
}
.add-filter-name:focus {
  outline: none;
}
.add-filter-option.is-checkbox {
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}
.add-filter-name {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.4rem;
  border: none;
  background: transparent;
  padding: 0.2rem 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-dark);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  border-radius: 6px;
}
.add-filter-icon {
  width: 14px;
  text-align: center;
  font-size: 0.78rem;
  color: var(--text-subtle);
  flex-shrink: 0;
}
.add-filter-inputs {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
  min-width: 0;
}
.add-filter-sublabel {
  font-size: 0.7rem;
  color: var(--text-light);
}
/* todos os campos com a mesma largura: a da coluna */
.add-filter-input {
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  height: 26px;
  padding: 0 0.4rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.78rem;
  box-sizing: border-box;
}
.add-filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
/* o período divide a mesma largura entre as duas datas */
.add-filter-input-date {
  flex: 1 1 0;
  width: auto;
}

.btn-pastel-blue {
  background-color: var(--focus-border);
  color: #1c3d5a;
}
.btn-pastel-blue:hover {
  background-color: #82b4ff;
}
.btn-pastel-green {
  background-color: #9bf6ff;
  color: #1a4a4f;
}
/* Ação de consulta (card de detalhes): neutra, para não competir com a ação
   primária da linha. O ícone fica em tom médio de propósito — o disco sólido do
   info-circle pesa muito mais que os três pontos do menu de ações, e um slate
   escuro fazia o botão de consulta gritar mais alto que o de comandos. */
.btn-slate {
  background-color: var(--neutral-200);
  color: var(--text-muted);
}
.btn-slate:hover {
  background-color: var(--neutral-400);
}

/* Espelhos da ancoragem, ligados por placeRowMenu quando falta espaço */
.row-dropdown-menu.opens-up {
  top: auto;
  bottom: 0;
}

.row-dropdown-menu.opens-left {
  left: auto;
  right: 42px;
}

.row-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}

.dropdown-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 6px 0;
}

.dropdown-toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.65rem;
  cursor: default;
}
.label-text {
  font-size: 0.8rem;
  color: var(--text-dark);
}
.row-dropdown-menu .toggle-switch {
  width: 38px;
  height: 20px;
}
.row-dropdown-menu .slider {
  border-radius: 999px;
}
.row-dropdown-menu .slider:before {
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
}
.row-dropdown-menu input:checked + .slider:before {
  transform: translateX(18px);
}

.table-wrapper {
  flex: 1 0 auto;
}

.user-pill-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 100%;
  justify-content: center;
}
.user-name {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sys-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.12rem 0.4rem;
  width: auto;
  white-space: nowrap;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(226, 232, 240, 0.8);
  color: rgba(51, 65, 85, 0.95);
}
td > .sys-pill {
  display: inline-flex !important;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--neutral-200);
  transition: 0.4s;
  border-radius: 28px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: var(--surface);
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: var(--brand-light);
}
input:checked + .slider:before {
  transform: translateX(22px);
}
input:disabled + .slider {
  cursor: not-allowed;
  opacity: 0.5;
}

.action-bar input,
.action-bar .search-input,
.search-text-input input {
  font-size: 0.8rem !important;
}

/* Desabilitado por permissão de vínculo: mantém pointer-events para o title/tooltip funcionar */
.btn-icon-disabled {
  opacity: 0.4;
  cursor: not-allowed !important;
}
.btn-icon-disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}
@keyframes btn-add-flash {
  0%   { box-shadow: none; }
  35%  { box-shadow: 0 0 0 3px #fde047, 0 0 12px 4px rgba(253, 224, 71, 0.7); }
  65%  { box-shadow: 0 0 0 3px #fde047, 0 0 12px 4px rgba(253, 224, 71, 0.7); }
  100% { box-shadow: none; }
}
.btn-primary--flash {
  animation: btn-add-flash 2.8s ease-in-out infinite;
}

.empty-icon--invite {
  opacity: 0.75;
}
.empty-hint {
  font-size: 0.85rem;
  color: var(--text-muted);
}
.empty-hint strong {
  color: var(--text-brand);
}

.search-text-wider {
  max-width: 360px;
  flex-shrink: 0;
}
.text-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.87rem;
}
.text-pagination-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.empty-state-content {
  width: 100%;
}

</style>